// The client of the /events/ws socket. It is the only socket of the page. The parent forwards
// each event to each registered iframe, and sends each `__eventsWs_send` frame of an iframe.
// See `Window["__eventsWs"]` in global.d.ts for the API.

type WsEvent = { kind: string; [key: string]: unknown };

const MAX_OUTBOUND_QUEUE = 100;
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 30000;

const handlers = new Set<(event: WsEvent) => void>();
const iframes = new Set<HTMLIFrameElement>();
const pendingSends: string[] = [];
let ws: WebSocket | null = null;
let connected = false;
let started = false;
let socketUrl: string | null = null;
let tokenProviderFn: (() => Promise<string>) | null = null;
let reconnectMs = BACKOFF_MIN_MS;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const clientId =
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Date.now().toString(36) + Math.random().toString(36).slice(2);

function emit(event: WsEvent) {
  handlers.forEach((h) => {
    try {
      h(event);
    } catch (err) {
      console.error("__eventsWs handler threw:", err);
    }
  });
  forwardToIframes(event);
}

function forwardToIframes(event: WsEvent) {
  iframes.forEach((frame) => {
    const w = frame.contentWindow;
    if (!w) return;
    try {
      w.postMessage(
        { type: "__eventsWs_event", event: event },
        window.location.origin,
      );
    } catch (err) {
      console.warn("__eventsWs iframe post failed:", err);
    }
  });
}

function flushPending() {
  while (pendingSends.length > 0 && connected && ws) {
    const frame = pendingSends.shift()!;
    try {
      ws.send(frame);
    } catch (err) {
      console.error("__eventsWs flush send failed:", err);
      pendingSends.unshift(frame);
      return;
    }
  }
}

function enqueueSend(frame: string) {
  if (pendingSends.length >= MAX_OUTBOUND_QUEUE) {
    const dropped = pendingSends.shift();
    console.warn("__eventsWs queue full; dropped oldest frame", dropped);
  }
  pendingSends.push(frame);
}

function connect() {
  if (!started || !tokenProviderFn || !socketUrl) return;
  if (ws && (ws.readyState === 0 || ws.readyState === 1)) return;

  let socket: WebSocket;
  try {
    socket = new WebSocket(socketUrl);
  } catch (err) {
    console.error("__eventsWs WebSocket ctor failed:", err);
    scheduleReconnect();
    return;
  }
  ws = socket;

  socket.onopen = async () => {
    try {
      const token = await tokenProviderFn!();
      socket.send(token);
    } catch (err) {
      console.error("__eventsWs token provider failed:", err);
      try {
        socket.close();
      } catch {
        // ignore
      }
      return;
    }
    connected = true;
    reconnectMs = BACKOFF_MIN_MS;
    emit({ kind: "__connected" });
    flushPending();
  };

  socket.onmessage = (msg) => {
    let parsed: WsEvent;
    try {
      parsed = JSON.parse(msg.data);
    } catch (err) {
      console.warn("__eventsWs failed to parse frame:", err);
      return;
    }
    emit(parsed);
  };

  socket.onclose = () => {
    const wasConnected = connected;
    connected = false;
    ws = null;
    if (wasConnected) emit({ kind: "__disconnected" });
    scheduleReconnect();
  };

  socket.onerror = (err) => {
    console.warn("__eventsWs socket error:", err);
  };
}

function scheduleReconnect() {
  if (!started) return;
  if (reconnectTimer) return;
  const delay = reconnectMs;
  reconnectMs = Math.min(reconnectMs * 2, BACKOFF_MAX_MS);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

window.addEventListener("message", (event) => {
  if (event.origin !== window.location.origin) return;
  const msg = event.data;
  if (!msg || typeof msg !== "object" || msg.type !== "__eventsWs_send") {
    return;
  }
  // Only accept frames from registered iframes.
  let fromRegistered = false;
  iframes.forEach((frame) => {
    if (frame.contentWindow === event.source) {
      fromRegistered = true;
    }
  });
  if (!fromRegistered) return;
  window.__eventsWs.send(msg.frame);
});

window.__eventsWs = {
  send(frame) {
    const payload = typeof frame === "string" ? frame : JSON.stringify(frame);
    if (connected && ws) {
      try {
        ws.send(payload);
        return;
      } catch (err) {
        console.warn("__eventsWs send failed, queuing:", err);
      }
    }
    enqueueSend(payload);
  },
  subscribe(handler) {
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
    };
  },
  isConnected() {
    return connected;
  },
  clientId() {
    return clientId;
  },
  // `url` is the full WebSocket URL. The page owns it, because Fastly does not
  // pass an upgrade on `/api` and only the page knows which API it targets.
  __start(url, provider) {
    if (typeof url !== "string" || !url) {
      throw new Error("__eventsWs.__start: url must be a non-empty string");
    }
    if (typeof provider !== "function") {
      throw new Error("__eventsWs.__start: provider must be a function");
    }
    socketUrl = url;
    tokenProviderFn = provider;
    if (started) return;
    started = true;
    connect();
  },
  __registerIframe(frame) {
    if (!frame || typeof frame !== "object") {
      return () => {};
    }
    iframes.add(frame);
    if (connected) {
      try {
        const w = frame.contentWindow;
        if (w) {
          w.postMessage(
            { type: "__eventsWs_event", event: { kind: "__connected" } },
            window.location.origin,
          );
        }
      } catch (err) {
        console.warn("__eventsWs replay __connected failed:", err);
      }
    }
    return () => {
      iframes.delete(frame);
    };
  },
};
